/**
 * Direct-to-storage upload: the server returns a short-lived URL, the browser
 * PUTs the file there with progress, then the feature attaches the uploadId.
 * XHR is used because fetch has no upload progress.
 */
import { api, unwrap } from './client';
import { ApiError } from './errors';
import { contentTypeOf, type UploadPurpose } from './upload-policy';

/** SHA-256 (hex) of the file, or undefined where Web Crypto is unavailable (non-secure context). */
async function sha256Hex(file: File): Promise<string | undefined> {
  // `crypto.subtle` is undefined outside secure contexts (and in some test environments).
  const subtle = (globalThis as { crypto?: { subtle?: SubtleCrypto } }).crypto?.subtle;
  if (!subtle) return undefined;
  try {
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

/** Headers the browser may not set itself (it computes Content-Length). */
const FORBIDDEN_HEADERS = new Set(['content-length', 'host']);

/**
 * Uploads a file for a purpose (the caller checked it with `checkUpload(file, purpose)` first) and returns the
 * uploadId the consuming command attaches.
 */
export async function uploadFile(file: File, onProgress: (percent: number) => void, signal?: AbortSignal, purpose: UploadPurpose = 'document'): Promise<string> {
  // The checksum lets the server (and S3) refuse bytes that differ from the announced file.
  const sha256 = await sha256Hex(file);
  const contentType = contentTypeOf(file, purpose);
  const intent = await unwrap(
    api.POST('/uploads', { body: { fileName: file.name, contentType, size: file.size, purpose, ...(sha256 ? { sha256 } : {}) }, signal }),
  );
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(intent.method ?? 'PUT', intent.uploadUrl);
    const headers = intent.headers ?? { 'Content-Type': contentType };
    for (const [name, value] of Object.entries(headers)) if (!FORBIDDEN_HEADERS.has(name.toLowerCase())) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError({ status: xhr.status, code: 'upload_failed', message: 'Upload failed', traceId: 'storage' }));
    };
    xhr.onerror = () => reject(new ApiError({ status: 0, code: 'network', message: 'Upload failed', traceId: 'storage' }));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });
  onProgress(100);
  return intent.uploadId;
}
